<script lang="ts">
import '@sg/src/View/Components/Atoms/Button/button.css';
import '@sg-core/src/View/Components/Atoms/Button/button.css';

import { PhxsgIcon } from '@sg/src/View/Components/Atoms/Icon';
import { PhxsgVideoPlayer } from '@sg/src/View/Components/Molecules/VideoPlayer';
import { useEmbeddableMedia } from '@sg-core/src/View/Components/Molecules/VideoPlayer';
import {
  PhxsgBackgroundVideo as PhxsgCoreBackgroundVideo,
  PhxsgBackgroundVideoControls,
  type PhxsgBackgroundVideoProps as CoreProps,
  type ControlsPosition,
} from '@sg-core/src/View/Components/Organisms/BackgroundVideo';
import { useNormalizedClass } from '@sg-core/src/View/Components/composables';

export type { ControlsPosition };

export interface PhxsgBackgroundVideoProps extends Omit<CoreProps, 'videoPlayerComponent'> {
  playLabel?: string;
  pauseLabel?: string;
  muteLabel?: string;
  unmuteLabel?: string;
  controlsPosition?: ControlsPosition;
}
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
  playLabel = 'Play',
  pauseLabel = 'Pause',
  muteLabel = 'Mute',
  unmuteLabel = 'Unmute',
  controlsPosition = 'bottom-end',
  embed,
  rootClass,
  ...coreProps
} = defineProps<PhxsgBackgroundVideoProps>();

const { isYoutube } = useEmbeddableMedia(() => embed);
const computedRootClass = useNormalizedClass(rootClass);
</script>

<template>
  <PhxsgCoreBackgroundVideo
    v-bind="{ ...coreProps, ...$attrs }"
    :embed="embed ?? undefined"
    :video-player-component="PhxsgVideoPlayer"
    :root-class="computedRootClass"
  >
    <PhxsgBackgroundVideoControls
      :position="controlsPosition"
      :loader="!isYoutube"
      class="phxsg-btn phxsg-btn--icon-only phxsg-btn--ghost"
    >
      <template #play="{ attrs }">
        <PhxsgIcon v-bind="attrs" id="phxsg-play" size="md" :title="playLabel" />
      </template>

      <template #pause="{ attrs }">
        <PhxsgIcon v-bind="attrs" id="phxsg-pause" size="md" :title="pauseLabel" />
      </template>

      <template #mute="{ attrs }">
        <PhxsgIcon v-bind="attrs" id="sound-mute" size="md" :title="muteLabel" />
      </template>

      <template #unmute="{ attrs }">
        <PhxsgIcon v-bind="attrs" id="sound" size="md" :title="unmuteLabel" />
      </template>
    </PhxsgBackgroundVideoControls>

    <slot />
  </PhxsgCoreBackgroundVideo>
</template>

<style src="./background-video.css" />
